import { useState, useEffect, useRef } from "react";
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/outsourced`;
const PRIMARY = "bg-[#5990DC]";

const systems = ['TB', 'YDL', 'WF', 'MC'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const defaultPeriod = () => {
  const n = new Date();
  return { mode: 'month', month: n.getMonth() + 1, year: n.getFullYear(), day: ymd(n), from: '', to: '' };
};

function periodBounds(p) {
  if (p.mode === 'month') {
    const last = new Date(p.year, p.month, 0).getDate();
    return { from: `${p.year}-${pad(p.month)}-01`, to: `${p.year}-${pad(p.month)}-${pad(last)}` };
  }
  if (p.mode === 'year') return { from: `${p.year}-01-01`, to: `${p.year}-12-31` };
  if (p.mode === 'day') return { from: p.day || '0000-01-01', to: p.day || '9999-12-31' };
  return { from: p.from || '0000-01-01', to: p.to || '9999-12-31' };
}

function periodLabel(p) {
  if (p.mode === 'month') return `${monthNames[p.month - 1]} ${p.year}`;
  if (p.mode === 'year') return `${p.year}`;
  if (p.mode === 'day') return p.day || 'Select a day';
  return `${p.from || 'start'} to ${p.to || 'today'}`;
}

function periodFileTag(p) {
  if (p.mode === 'month') return `${p.year}-${pad(p.month)}`;
  if (p.mode === 'year') return `${p.year}`;
  if (p.mode === 'day') return p.day || 'day';
  return `${p.from || 'start'}_to_${p.to || 'end'}`;
}

const fmtShort = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
const fmtLong = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '-');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-');
const userName = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').name || ''; } catch (e) { return ''; }
};

function PeriodControls({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 3 + i);
  const update = (patch) => onChange({ ...value, ...patch });
  return (
    <div>
      <label className="text-xs font-bold text-gray-700 mb-1.5 block">Period</label>
      <div className="flex gap-1.5">
        <select value={value.mode} onChange={(e) => update({ mode: e.target.value })} className="border rounded-lg px-2 py-2 text-sm">
          <option value="month">Month</option>
          <option value="year">Year</option>
          <option value="day">Day</option>
          <option value="range">Custom</option>
        </select>
        {value.mode === 'month' && (
          <>
            <select value={value.month} onChange={(e) => update({ month: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0">
              {monthNames.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
            </select>
            <select value={value.year} onChange={(e) => update({ year: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm">
              {years.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </>
        )}
        {value.mode === 'year' && (
          <select value={value.year} onChange={(e) => update({ year: parseInt(e.target.value, 10) })} className="border rounded-lg px-2 py-2 text-sm flex-1">
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        )}
        {value.mode === 'day' && (
          <input type="date" value={value.day} onChange={(e) => update({ day: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
        )}
        {value.mode === 'range' && (
          <>
            <input type="date" value={value.from} onChange={(e) => update({ from: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
            <input type="date" value={value.to} onChange={(e) => update({ to: e.target.value })} className="border rounded-lg px-2 py-2 text-sm flex-1 min-w-0" />
          </>
        )}
      </div>
    </div>
  );
}

function AccountsBanner({ accounts }) {
  const down = (accounts || []).filter((a) => !a.ok);
  if (down.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
      {down.map((a) => a.system).join(', ')} could not be reached, so {down.length > 1 ? 'their' : 'its'} numbers are not included right now. Press Refresh to try again.
    </div>
  );
}

const allColumns = [
  { key: 'date', label: 'Date' },
  { key: 'batch', label: 'Batch' },
  { key: 'TB', label: 'TB' },
  { key: 'YDL', label: 'YDL' },
  { key: 'WF', label: 'WF' },
  { key: 'MC', label: 'MC' },
  { key: 'total', label: 'Total' },
  { key: 'pdfs', label: 'PDFs' },
  { key: 'status', label: 'Status' },
];

function ReportView() {
  const [period, setPeriod] = useState(defaultPeriod());
  const [account, setAccount] = useState('');
  const [batch, setBatch] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [showDownload, setShowDownload] = useState(false);
  const [selectedCols, setSelectedCols] = useState(allColumns.map((c) => c.key));
  const [includeTotals, setIncludeTotals] = useState(true);
  const [format, setFormat] = useState('xlsx');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/overview`, { cache: 'no-store' });
      const data = await res.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
    } catch (e) {
      setRows([]);
      setAccounts([]);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const { from, to } = periodBounds(period);
  const q = search.trim().toLowerCase();

  const view = rows
    .map((r) => {
      const files = account ? r.files.filter((f) => f.system === account) : r.files;
      const vals = {};
      systems.forEach((s) => { vals[s] = account && s !== account ? 0 : r[s]; });
      const total = r.historical ? r.total : systems.reduce((sum, s) => sum + vals[s], 0);
      return { ...r, ...vals, files, total, pdfs: files.length };
    })
    .filter((r) => {
      if (r.date < from || r.date > to) return false;
      if (account && r.files.length === 0) return false;
      if (batch && String(r.batchNumber) !== batch) return false;
      if (status && r.status !== status) return false;
      if (q) {
        const hit = r.files.some((f) => String(f.name).toLowerCase().includes(q) || String(f.displayName || '').toLowerCase().includes(q));
        if (!hit && !fmtLong(r.date).toLowerCase().includes(q)) return false;
      }
      return true;
    });

  const batchOptions = Array.from(new Set(rows.map((r) => r.batchNumber))).sort((a, b) => a - b);
  const systemTotals = {};
  systems.forEach((s) => { systemTotals[s] = view.reduce((sum, r) => sum + r[s], 0); });
  const grandTotal = view.reduce((sum, r) => sum + r.total, 0);
  const totalPdfs = view.reduce((sum, r) => sum + r.pdfs, 0);
  const historicalTotal = view.reduce((sum, r) => sum + (r.historical ? r.total : 0), 0);

  const copyRow = (r) => {
    navigator.clipboard.writeText(r.historical ? `TOTAL SHEET = ${r.total} SHEETS` : `TOTAL TB/YDL/WF/MC SHEET = ${r.TB}+${r.YDL}+${r.WF}+${r.MC} = ${r.total} SHEETS`);
  };

  const toggleCol = (key) =>
    setSelectedCols((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const cellValue = (r, key) => {
    if (key === 'date') return fmtLong(r.date);
    if (key === 'batch') return `B${r.batchNumber}`;
    if (r.historical && (systems.includes(key) || key === 'pdfs')) return '';
    return r[key];
  };

  const handleDownload = async () => {
    const cols = allColumns.filter((c) => selectedCols.includes(c.key));
    if (cols.length === 0 || view.length === 0) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const body = view.map((r) => cols.map((c) => cellValue(r, c.key)));
      if (includeTotals) {
        body.push(cols.map((c, i) => {
          if (c.key === 'total') return grandTotal;
          if (c.key === 'pdfs') return totalPdfs;
          if (systems.includes(c.key)) return systemTotals[c.key];
          return i === 0 ? 'TOTAL' : '';
        }));
      }
      const res = await fetch(`${BASE}/report-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, title: 'Outsourced Work Report', subtitle: periodLabel(period), headers: cols.map((c) => c.label), rows: body, hasTotalRow: includeTotals }),
      });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outsourced-report-${periodFileTag(period)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setShowDownload(false);
    } catch (e) {
      setDownloadError('Download failed — please try again.');
    }
    setDownloading(false);
  };

  const bySystemThenName = (a, b) => systems.indexOf(a.system) - systems.indexOf(b.system) || String(a.name).localeCompare(String(b.name));

  return (
    <div>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-xl font-semibold mb-1">Outsourced Work Report</h1>
          <p className="text-gray-500 text-sm">Date → Batch → Files. Shows only syncs confirmed inside each production system.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setDownloadError(''); setShowDownload(true); }} className="border border-gray-300 bg-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-gray-50">
            ⬇ Download Report
          </button>
          <button onClick={load} disabled={loading} className={`${PRIMARY} text-white rounded-lg px-6 py-2.5 text-sm font-medium disabled:opacity-50`}>
            {loading ? 'Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>
      <div className="mb-4"></div>

      <AccountsBanner accounts={accounts} />

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[320px]">
            <PeriodControls value={period} onChange={setPeriod} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Account</label>
            <select value={account} onChange={(e) => setAccount(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All Accounts</option>
              {systems.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Batch</label>
            <select value={batch} onChange={(e) => setBatch(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All</option>
              {batchOptions.map((b) => (<option key={b} value={b}>B{b}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">All</option>
              <option value="Synced">Synced</option>
              <option value="Source deleted">Source deleted</option>
              <option value="Historical">Historical</option>
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs font-bold text-gray-700 mb-1.5 block">Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="File name or date..." className="border rounded-lg px-3 py-2 text-sm w-full" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-4 mb-6">
        {systems.map((s) => (
          <div key={s} className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500">{s} Total Sheets</p>
            <p className="text-2xl font-semibold mt-1">{systemTotals[s]}</p>
          </div>
        ))}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500">Before account tracking</p>
          <p className="text-2xl font-semibold mt-1">{historicalTotal}</p>
        </div>
        <div className="bg-gray-900 text-white rounded-xl p-4">
          <p className="text-xs text-gray-300">Grand Total</p>
          <p className="text-2xl font-semibold mt-1">{grandTotal}</p>
          <p className="text-xs text-gray-400">{totalPdfs} PDFs</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
              <th className="text-left px-4 py-3 font-normal">Date</th>
              <th className="text-left px-4 py-3 font-normal">Batch</th>
              <th className="text-right px-4 py-3 font-normal">TB</th>
              <th className="text-right px-4 py-3 font-normal">YDL</th>
              <th className="text-right px-4 py-3 font-normal">WF</th>
              <th className="text-right px-4 py-3 font-normal">MC</th>
              <th className="text-right px-4 py-3 font-normal">Total</th>
              <th className="text-right px-4 py-3 font-normal">PDFs</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-left px-4 py-3 font-normal"></th>
            </tr>
          </thead>
          {loading ? (
            <tbody><tr><td colSpan={10} className="text-center text-gray-400 py-10">Loading...</td></tr></tbody>
          ) : view.length === 0 ? (
            <tbody><tr><td colSpan={10} className="text-center text-gray-400 py-10">No confirmed syncs match these filters.</td></tr></tbody>
          ) : (
            view.map((r) => {
              const key = `${r.historical ? 'H::' : ''}${r.date}::${r.batchNumber}`;
              const isOpen = open === key;
              return (
                <tbody key={key}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3">{fmtShort(r.date)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setOpen(isOpen ? null : key)} className="text-blue-600 underline">B{r.batchNumber}</button>
                    </td>
                    <td className="px-4 py-3 text-right">{r.historical ? '-' : r.TB}</td>
                    <td className="px-4 py-3 text-right">{r.historical ? '-' : r.YDL}</td>
                    <td className="px-4 py-3 text-right">{r.historical ? '-' : r.WF}</td>
                    <td className="px-4 py-3 text-right">{r.historical ? '-' : r.MC}</td>
                    <td className="px-4 py-3 text-right font-bold">
                      <button onClick={() => setOpen(isOpen ? null : key)} className="underline">{r.total}</button>
                    </td>
                    <td className="px-4 py-3 text-right">{r.historical ? '-' : r.pdfs}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs rounded-full px-3 py-1 ${r.status === 'Synced' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => copyRow(r)} className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50">Copy</button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <td colSpan={10} className="px-6 py-3">
                        <p className="text-xs font-semibold mb-2">{fmtLong(r.date)} · B{r.batchNumber} · file details</p>
                        {r.historical && (<p className="text-xs text-gray-500 mb-2">Imported total from your records. It has no per-account or file detail.</p>)}
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500">
                              <th className="text-left py-1 font-normal">Account</th>
                              <th className="text-left py-1 font-normal">File</th>
                              <th className="text-right py-1 font-normal">Orders</th>
                              <th className="text-right py-1 font-normal">Sheets</th>
                              <th className="text-left py-1 pl-4 font-normal">Source</th>
                              <th className="text-left py-1 font-normal">Synced</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...r.files].sort(bySystemThenName).map((f) => (
                              <tr key={f.fileUid} className="border-t border-gray-200">
                                <td className="py-1.5 font-medium">{f.system}</td>
                                <td className="py-1.5">{f.name}</td>
                                <td className="py-1.5 text-right">{f.orders}</td>
                                <td className="py-1.5 text-right">{f.sheets}</td>
                                <td className={`py-1.5 pl-4 ${f.sourceDeleted ? 'text-amber-700' : 'text-gray-500'}`}>
                                  {f.sourceDeleted ? 'Source file deleted from Production' : 'In Production'}
                                </td>
                                <td className="py-1.5 text-gray-500">{fmtStamp(f.syncedAt)}</td>
                              </tr>
                            ))}
                            <tr className="border-t border-gray-300 font-semibold">
                              <td className="py-1.5" colSpan={3}>Total</td>
                              <td className="py-1.5 text-right">{r.total}</td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })
          )}
        </table>
      </div>

      {showDownload && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowDownload(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold mb-1">Download Report</h2>
            <p className="text-xs text-gray-500 mb-4">Tick the columns you want in the file</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {allColumns.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={selectedCols.includes(c.key)} onChange={() => toggleCol(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-4">
              <input type="checkbox" checked={includeTotals} onChange={(e) => setIncludeTotals(e.target.checked)} />
              Add a TOTAL row at the bottom
            </label>
            <div className="mb-3">
              <label className="text-xs font-bold text-gray-700 mb-1 block">Format</label>
              <select value={format} onChange={(e) => setFormat(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-full">
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
                <option value="pdf">PDF (.pdf)</option>
              </select>
            </div>
            <p className="text-xs text-gray-400 mb-4">Period: {periodLabel(period)} — {view.length} row(s)</p>
            {downloadError && <p className="text-xs text-red-600 mb-3">{downloadError}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDownload(false)} className="border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={handleDownload} disabled={downloading || selectedCols.length === 0 || view.length === 0} className={`${PRIMARY} text-white rounded-lg px-4 py-2 text-sm disabled:opacity-50`}>
                {downloading ? 'Preparing...' : 'Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportView;
